import { Cog6ToothIcon } from '@heroicons/react/24/outline'
import {
  Button,
  Card,
  CardBody,
  Divider,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  Input,
  Select,
  SelectItem,
  Textarea,
} from '@heroui/react'
import RequestData from 'components/Dashboard/Parameters/RequestData'
import PathwayVersion from 'components/Hero/PathwayVersion'
import { useContext, useEffect, useState } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import { $fetch } from 'utils/fetch'
import { getAuthToken } from 'utils/funcs/browser/getAuthToken'
import FlowContext from '../contextFlow'
import { DEFAULT_TEST_PROFILES } from './test_profiles/defaultTestProfiles'
import { useSimulation } from './test_profiles/useSimulation'
import TestProfiles from './TestProfiles'

export default function TestPathwayDrawer({
  isOpen,
  onClose,
  versions,
  elements,
  onSave,
  onTest,
  chatStartNode,
  chatRequestData,
  setChatStartNode,
  setChatRequestData,
  chatEndpoint,
  setChatEndpoint,
  enableQA,
  setEnableQA,
  setChatVersion,
}) {
  const [currentSection, setCurrentSection] = useState('main')
  const [showAdvancedInstructions, setShowAdvancedInstructions] = useState(false)
  const [savedConfigs, setSavedConfigs] = useState([])
  const [configName, setConfigName] = useState('')
  const [selectedConfig, setSelectedConfig] = useState(null)
  const [editingConfig, setEditingConfig] = useState(null)
  const [customObjectives, setCustomObjectives] = useState({})
  const [simulationId, setSimulationId] = useState(null)
  const [selectedProfiles, setSelectedProfiles] = useState(() =>
    new Set(DEFAULT_TEST_PROFILES.map(profile => profile.name)),
  )
  const [customProfiles, setCustomProfiles] = useState([])

  const token = getAuthToken()

  const { triggerUpdate, id: pathwayId, selectedVersion } = useContext(FlowContext)

  const {
    runSimulation,
    isRunningSimulation,
    useSimulationData,
  } = useSimulation()

  const { data: simulationData } = useSimulationData(simulationId, {
    onSuccess: (data) => {
      const allComplete = data?.personas?.every(p =>
        p.status === 'completed'
        || p.status === 'failed'
        || p.status === 'max_turns_reached',
      )

      if (allComplete) {
        toast.info('All test profiles have completed their conversations')
      }
    },
  })

  const methods = useForm({
    defaultValues: {
      preset: '',
      start_node_id: elements.nodes.find(node => node.id === chatStartNode)
        || elements.nodes.find(node => node.data.isStart)
        || elements.nodes[0],
      request_data: chatRequestData || [],
      pathway_version: selectedVersion?.version_number || versions?.[0]?.version_number || null,
      endpoint: chatEndpoint || null,
      testType: 'standard',
      unitTest: false,
      instructions: '',
      customObjective: '',
      numberOfTests: 5,
      enableQA: enableQA || false,
    },
    mode: 'onChange',
  })

  const { watch, handleSubmit, setValue, control, getValues } = methods
  const testType = watch('testType')
  const instructions = watch('instructions')
  const numberOfTests = watch('numberOfTests')

  useEffect(() => {
    if (selectedVersion?.version_number) {
      setValue('pathway_version', selectedVersion.version_number)
    }
  }, [selectedVersion, setValue])

  useEffect(() => {
    if (isOpen) {
      loadSavedConfigs()
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) {
      setCurrentSection('main')
    }
  }, [isOpen])

  const loadSavedConfigs = async () => {
    try {
      const response = await $fetch('/call_config/read', {
        headers: {
          Authorization: token,
        },
      })
      if (response.errors)
        throw new Error('Failed to fetch configurations')
      const configs = response.data
      setSavedConfigs([...configs])
    }
    catch (error) {
      console.error('Error loading configurations:', error)
      toast.error('Failed to load saved configurations')
    }
  }

  const saveConfig = async () => {
    if (!configName) {
      toast.error('Please enter a name for your configuration')
      return
    }
    try {
      const configData = {
        name: configName,
        call_config: methods.getValues(),
      }

      let response
      if (editingConfig) {
        response = await $fetch('/call_config', {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': token,
          },
          body: JSON.stringify({ ...configData, id: editingConfig.id }),
        })
      }
      else {
        response = await $fetch('/call_config', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': token,
          },
          body: JSON.stringify(configData),
        })
      }

      if (response.errors)
        throw new Error('Failed to save configuration')
      const savedConfig = response.data

      setSavedConfigs((prev) => {
        const index = prev.findIndex(c => c.id === savedConfig.id)
        if (index !== -1) {
          return prev.map(c => (c.id === savedConfig.id ? savedConfig : c))
        }
        else {
          return [...prev, savedConfig]
        }
      })
      setSelectedConfig(savedConfig)
      setEditingConfig(savedConfig)
      toast.success(
        `Configuration ${editingConfig ? 'updated' : 'created'} successfully`,
      )
    }
    catch (error) {
      console.error('Error saving configuration:', error)
      toast.error('Failed to save configuration')
    }
  }

  const loadConfig = (config) => {
    methods.reset(config.call_config)
    setSelectedConfig(config)
  }

  const deleteConfig = async (configId) => {
    try {
      const response = await $fetch(`/call_config`, {
        method: 'DELETE',
        headers: {
          Authorization: token,
        },
        body: JSON.stringify({ id: configId }),
      })

      if (response.errors) {
        throw new Error('Failed to delete config')
      }
      setSavedConfigs(prev =>
        prev.filter(config => config.id !== configId),
      )
      if (selectedConfig?.id === configId) {
        setSelectedConfig(null)
        setEditingConfig(null)
      }
      toast.success('Configuration deleted successfully')
    }
    catch (error) {
      console.error('Error deleting configuration:', error)
      toast.error('Failed to delete configuration')
    }
  }

  const startEditing = (config) => {
    setConfigName(config.name)
    setEditingConfig(config)
    methods.reset(config.call_config)
  }

  const handleStartTesting = (data) => {
    console.log('formValues', getValues())
    if (data.testType === 'manual') {
      // Handle manual test similar to TestPathway.jsx
      const formValues = methods.getValues()
      setChatStartNode(formValues.start_node_id?.id)
      setChatRequestData(formValues.request_data)
      setChatEndpoint(formValues.endpoint)
      setEnableQA(formValues.enableQA)
      setChatVersion(formValues.pathway_version)

      const newNodes = elements.nodes.map(node => ({
        ...node,
        data: {
          ...node.data,
          active: node.id === formValues.start_node_id?.id,
        },
      }))
      triggerUpdate({ nodes: newNodes }, true)
      onClose()
      onTest(data)
    }
    else if (data.testType === 'standard') {
      // Start automated test immediately with selected default profiles
      const formValues = getValues()

      // Get all profiles (default + custom) that are selected
      const personas = [...DEFAULT_TEST_PROFILES, ...customProfiles]
        .filter(profile => selectedProfiles.has(profile.name))
        .map(profile => ({
          name: profile.name,
          objective: customObjectives[profile.name] || profile.objective,
        }))

      if (personas.length === 0) {
        toast.error('Please select at least one test profile')
        return
      }

      // Run the simulation with selected profiles
      runSimulation({
        pathway_id: pathwayId,
        version_number: formValues.pathway_version ?? '0',
        max_turns: formValues.numberOfTests || 5,
        personas,
        advanced_instructions: formValues.instructions || undefined,
        request_data: formValues.request_data,
        start_node_id: formValues.start_node_id?.id,
      }, {
        onSuccess: (data) => {
          setSimulationId(data.simulation_id)
          toast.success('Test profiles are now running!')
          onClose()

          onTest({
            ...data,
            testType: 'standard',
            simulationId: data.simulation_id,
            request_data: formValues.request_data,
            start_node_id: formValues.start_node_id?.id,
            version_number: formValues.pathway_version ?? '0',
          })
        },
      })
    }
    else if (currentSection === 'main') {
      setCurrentSection('config')
    }
    else {
      console.log('Starting automated test with data:', data)
      startAutomatedTest(data)
    }
  }

  const startAutomatedTest = (data) => {
    const formValues = getValues()

    // Get all profiles (default + custom) that are selected
    const personas = [...DEFAULT_TEST_PROFILES, ...customProfiles]
      .filter(profile => selectedProfiles.has(profile.name))
      .map(profile => ({
        name: profile.name,
        objective: customObjectives[profile.name] || profile.objective,
      }))

    if (personas.length === 0) {
      toast.error('Please select at least one test profile')
      return
    }

    // Run the simulation
    runSimulation({
      pathway_id: pathwayId,
      version_number: formValues.pathway_version ?? '0',
      max_turns: formValues.numberOfTests,
      personas,
      request_data: formValues.request_data,
      start_node_id: formValues.start_node_id?.id,
      instructions: formValues.instructions || undefined, // Only include if set
    }, {
      onSuccess: (data) => {
        // Store the simulation ID
        setSimulationId(data.simulation_id)
        toast.success('Test profiles are now running!')

        // Close the drawer
        onClose()

        // Trigger any post-test actions if needed
        onTest({
          ...data,
          testType: 'standard',
          simulationId: data.simulation_id,
          request_data: formValues.request_data,
          start_node_id: formValues.start_node_id?.id,
          version_number: formValues.pathway_version ?? '0',
        })
      },
    })
  }

  const handleBack = () => {
    setCurrentSection('main')
  }

  const toggleAdvancedInstructions = (e) => {
    e.preventDefault()
    setShowAdvancedInstructions(!showAdvancedInstructions)
  }

  const handleObjectiveChange = (profileName, value) => {
    setCustomObjectives(prev => ({
      ...prev,
      [profileName]: value,
    }))
  }

  const renderConfigurationSection = () => {
    const selectedProfileCount = selectedProfiles.size
    const isOverLimit = selectedProfileCount > 5
    const isUnderLimit = selectedProfileCount === 0

    return (
      <div className="flex flex-col h-full">
        <div className="flex-1 flex flex-col gap-6 overflow-y-auto pb-20">
          {isOverLimit && (
            <div className="bg-red-50 text-red-600 p-4 rounded-md text-sm">
              Please select no more than 5 test profiles to proceed.
            </div>
          )}
          {isUnderLimit && (
            <div className="bg-yellow-50 text-yellow-600 p-4 rounded-md text-sm">
              Please select at least one test profile to proceed.
            </div>
          )}

          <TestProfiles
            onObjectiveChange={handleObjectiveChange}
            customObjectives={customObjectives}
            onSelectedProfilesChange={setSelectedProfiles}
            selectedProfiles={selectedProfiles}
            customProfiles={customProfiles}
            onCustomProfilesChange={setCustomProfiles}
          />

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-base font-medium text-black">Advanced Instructions</label>

              <button
                onClick={toggleAdvancedInstructions}
                className={`p-1.5 rounded-full ${showAdvancedInstructions ? 'bg-gray-100' : 'hover:bg-gray-100'}`}
              >
                <Cog6ToothIcon className="w-5 h-5 text-gray-700" />
              </button>
            </div>
            {showAdvancedInstructions && (
              <p className="text-xs text-gray-500">Add general instructions for the test profiles, for what you want to focus the test on.</p>
            )}

            {showAdvancedInstructions && (
              <Textarea
                placeholder={`Additional general instructions for AI test profiles... \ne.g. 'Focus on testing how the agent handles dates'`}
                value={instructions}
                onChange={e => setValue('instructions', e.target.value)}
                className="min-h-[120px]"
                radius="none"
              />
            )}
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-black">Max Number of Conversation Turns</label>
            <Input
              type="number"
              min={1}
              max={6}
              value={numberOfTests}
              onChange={e => setValue('numberOfTests', Number.parseInt(e.target.value))}
              className="w-32"
            />
            <p className="text-xs text-gray-500">
              {testType === 'standard'
                ? 'You can re-trigger the profile to continue the conversation later.'
                : 'Maximum 3 messages for custom testing'}
            </p>
          </div>
        </div>

        <div className="flex gap-3 bottom-0 right-0 bg-white pt-4 pb-4 border-t border-gray-200 px-6">
          <Button
            variant="bordered"
            radius="none"
            className="flex-1 border-gray-200 text-black hover:bg-gray-50"
            onPress={handleBack}
          >
            Back
          </Button>
          <Button
            type="button"
            radius="none"
            className="flex-1 bg-black text-white hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
            onPress={handleSubmit(handleStartTesting)}
            isLoading={isRunningSimulation}
            isDisabled={isOverLimit || isUnderLimit}
          >
            {isRunningSimulation ? 'Starting Test...' : 'Test Pathway'}
          </Button>
        </div>
      </div>
    )
  }

  const renderMainSection = () => (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(handleStartTesting)} className="flex flex-col gap-6">
        <div className="space-y-2">
          <label className="text-sm font-medium text-black">Select Configuration Preset</label>
          <Select
            placeholder="Select Preset"
            value={watch('preset')}
            onChange={value => setValue('preset', value)}
            radius="none"
            variant="bordered"
            classNames={{
              trigger: 'bg-white border-gray-200 text-black',
              value: 'text-black',
            }}
          >
            <SelectItem key="preset-1">Configuration 1</SelectItem>
            <SelectItem key="preset-2">Configuration 2</SelectItem>
          </Select>
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-black">Start Node</label>
          <Select
            placeholder="Select Node"
            value={watch('start_node_id')?.id}
            onChange={(value) => {
              const selectedNode = elements.nodes.find(node => node.id === value)
              setValue('start_node_id', selectedNode)
            }}
            radius="none"
            variant="bordered"
            classNames={{
              trigger: 'bg-white border-gray-200 text-black',
              value: 'text-black',
            }}
          >
            {elements?.nodes?.map(node => (
              <SelectItem key={node.id} value={node.id}>
                {node.data.name || 'Unnamed Node'}
              </SelectItem>
            ))}
          </Select>
        </div>

        <div className="space-y-2">
          <Controller
            name="pathway_version"
            control={control}
            render={({ field }) => (
              <PathwayVersion
                value={field.value}
                pathwayId={pathwayId}
                onChange={field.onChange}
                classNames={{
                  trigger: 'bg-white border-gray-200 text-black',
                  value: 'text-black',
                }}
              />
            )}
          />
        </div>

        <RequestData fieldName="request_data" short />

        <Divider className="bg-gray-200 -mt-1" />

        <div className="space-y-4">
          <h3 className="text-sm font-medium text-black">Select Test Type</h3>

          <Controller
            name="testType"
            control={control}
            render={({ field }) => (
              <>
                <Card
                  isPressable
                  isSelected={field.value === 'standard'}
                  onPress={() => field.onChange('standard')}
                  radius="none"
                  className={field.value === 'standard' ? 'border-2 border-black bg-gray-50' : 'border border-gray-200'}
                >
                  <CardBody>
                    <div className="flex justify-between items-center">
                      <div>
                        <h4 className="text-sm font-medium text-black">Bland Test Profiles</h4>
                        <p className="text-sm text-gray-500">
                          Deploy multiple test profiles to test your pathway in different scenarios at the same time.
                        </p>
                      </div>
                      <span className="text-xs text-green-600">Recommended</span>
                    </div>
                  </CardBody>
                </Card>

                <Card
                  isPressable
                  isSelected={field.value === 'manual'}
                  onPress={() => field.onChange('manual')}
                  radius="none"
                  className={field.value === 'manual' ? 'border-2 border-black bg-gray-50' : 'border border-gray-200'}
                >
                  <CardBody>
                    <div>
                      <h4 className="text-sm font-medium text-black">Manual Test</h4>
                      <p className="text-sm text-gray-500">
                        Test the pathway manually by interacting with it directly as an end user would.
                      </p>
                    </div>
                  </CardBody>
                </Card>
              </>
            )}
          />
        </div>

        <div className="flex gap-3 sticky bottom-0 left-0 right-0 bg-white pt-4 border-t border-gray-200">
          {testType === 'manual'
            ? (
                <Button
                  type="submit"
                  radius="none"
                  className="flex-1 bg-black text-white hover:bg-gray-800"
                  isLoading={isRunningSimulation}
                >
                  {isRunningSimulation ? 'Starting Test...' : 'Test Pathway'}
                </Button>
              )
            : (
                <>
                  <Button
                    type="button"
                    radius="none"
                    className="flex-1 bg-white text-gray-700 border-2 border-gray-300 hover:bg-gray-50"
                    onPress={() => setCurrentSection('config')}
                  >
                    Configure Profiles
                  </Button>
                  <Button
                    type="submit"
                    radius="none"
                    className="flex-1 bg-black text-white hover:bg-gray-800"
                    isLoading={isRunningSimulation}
                  >
                    {isRunningSimulation ? 'Starting Test...' : 'Test Pathway'}
                  </Button>
                </>
              )}
        </div>
      </form>
    </FormProvider>
  )

  return (
    <Drawer
      isOpen={isOpen}
      onOpenChange={onClose}
      size="md"
      classNames={{
        base: 'bg-white border-l border-gray-200 z-[40]',
        header: 'border-b border-gray-200',
        body: 'p-6',
      }}
      radius="none"
      backdrop="transparent"
      motionProps={{
        variants: {
          enter: {
            x: 0,
            transition: {
              duration: 0.4,
              ease: 'easeOut',
            },
          },
          exit: {
            x: '100%',
            transition: {
              duration: 0.4,
              ease: 'easeIn',
            },
          },
        },
      }}
    >
      <DrawerContent>
        <DrawerHeader className="flex flex-col gap-1">
          <h2 className="text-xl font-semibold text-black">{currentSection === 'main' ? 'Test Pathway' : 'Test Pathway Configuration'}</h2>
        </DrawerHeader>
        <DrawerBody>
          {currentSection === 'main' ? renderMainSection() : renderConfigurationSection()}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  )
}
